import React, {
 createContext, memo, useCallback, useContext, useMemo
} from 'react';
import { useTranslation } from 'react-i18next';

import { useRequest } from '@common/services/api';
import { AlertService } from '@common/services/alert';

import type { CustomUserPropertiesResponse, CustomUserProperty } from '../types';
import {
  buildCustomUserPropertyPredicateFilters,
  type CustomUserPropertyPredicateFilter,
} from '../utils/build-custom-user-property-predicate-filters';

type CustomUserPropertiesContextValue = {
  organisationId: string | undefined;
  properties: CustomUserProperty[];
  propertiesById: Map<string, CustomUserProperty>;
  filtersMap: Record<string, CustomUserPropertyPredicateFilter>;
  loading: boolean;
  error: Error | null;
};

const EMPTY_PROPERTIES_BY_ID = new Map<string, CustomUserProperty>();
const EMPTY_FILTERS_MAP: Record<string, CustomUserPropertyPredicateFilter> = {};

const CustomUserPropertiesContext = createContext<CustomUserPropertiesContextValue | null>(null);

export function useCustomUserPropertiesContextOptional() {
  return useContext(CustomUserPropertiesContext);
}

export const CustomUserPropertiesProvider = memo(({
  organisationId,
  enabled = false,
  children,
}: React.PropsWithChildren<{ organisationId: string | undefined; enabled?: boolean }>) => {
  const { t } = useTranslation();

  const endpoint = useMemo(
    () => (organisationId ? `/v1/organisations/${organisationId}/user-properties` : ''),
    [organisationId]
  );

  const handleLoadError = useCallback(() => {
    AlertService.error(t('organisation:user_properties_load_error'));
  }, [t]);

  const {
    data: properties = [],
    loading,
    error,
  } = useRequest<CustomUserPropertiesResponse>(endpoint, {
    enabled: enabled && !!organisationId,
    onError: handleLoadError,
  });

  const filtersMap = useMemo(
    () => (enabled ? buildCustomUserPropertyPredicateFilters(properties) : EMPTY_FILTERS_MAP),
    [enabled, properties]
  );

  const propertiesById = useMemo(
    () => (enabled
      ? new Map(properties.map((property) => [property.id, property]))
      : EMPTY_PROPERTIES_BY_ID),
    [enabled, properties]
  );

  const value = useMemo<CustomUserPropertiesContextValue>(() => ({
    organisationId,
    properties: enabled ? properties : [],
    propertiesById,
    filtersMap,
    loading: enabled ? loading : false,
    error: enabled ? ((error as Error | null) ?? null) : null,
  }), [organisationId, enabled, properties, propertiesById, filtersMap, loading, error]);

  return (
    <CustomUserPropertiesContext.Provider value={value}>
      {children}
    </CustomUserPropertiesContext.Provider>
  );
});

CustomUserPropertiesProvider.displayName = 'CustomUserPropertiesProvider';

